import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { COOLING_BED_PRODUCTION_LABEL } from "@/setup/mill-config/mill-config";
import type { ShiftIncentiveData } from "@/pages/incentive/models/ShiftIncentiveData";
import { KPISection } from "@/shared/components/KPISection";
import type { KPIThresholds } from "@/shared/components/KPICard";
import { pageBgColor } from "@/utils/theme";
import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	routeLogbookEntry,
	useHasPermission,
	useMesfRealtime,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Box, Button, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAssetId } from "@/setup/hooks/useAssetId";
import {
	GanttChart,
	type GapTimeData,
} from "@/pages/mill/gantt/components/GanttChart";
import {
	Delay,
	DmhEvent,
	Production,
	RollChange,
} from "@/pages/mill/gantt/models/GanttReponse";
import { M1ProductionStats } from "./components/M1ProductionStats";
import { M1ShiftReportSkeleton } from "./components/M1ShiftReportSkeleton";
import { TableM1StackerBundles } from "./components/TableM1StackerBundles";
import { PushRateCrewChart } from "@/pages/shift-report/components/PushRateCrewChart";
import { IncentiveCard } from "@/pages/shift-report/components/IncentiveCard";
import { RollingTimeCard } from "@/pages/shift-report/components/RollingTimeCard";
import { TableDelays } from "@/pages/shift-report/components/tables/delays/TableDelays";
import { TableHeats } from "@/pages/shift-report/components/tables/heats/TableHeats";
import { TableLogbook } from "@/pages/shift-report/components/tables/logbook/TableLogbook";
import { TableProductionSummary } from "@/pages/shift-report/components/tables/production-summary/TableProductionSummary";
import { M1ShiftReport } from "./models/M1ShiftReportResponse";
import { getM1ShiftReport } from "./repositories/M1ShiftReportRepository";

const M1_SHIFT_REPORT_ROUTE = "/m1/shift-report";
// M1 AssetId for logbook and delays — matches backend M1 asset (GetData.cs uses 11)
const M1_LOGBOOK_ASSET_ID = 11;

// Hidden temporarily — not tracked in M1 yet
const M1_HIDDEN_COLUMNS = [
	"Rejects",
	"Cobbles",
	"Charged Yield Loss",
	"Rolled Yield Loss",
];

const incentiveThresholds = (d: ShiftIncentiveData): KPIThresholds => ({
	red: d.IncRed,
	orange: d.IncOrange,
	yellow: d.IncYellow,
	greenSoft: d.IncGreenSoft,
	greenNormal: d.IncGreenNormal,
	greenHard: d.IncGreenHard,
});

const emptyIncentiveThresholds: KPIThresholds = {
	red: 0,
	orange: 0,
	yellow: 0,
	greenSoft: 0,
	greenNormal: 0,
	greenHard: 0,
};

export const useM1ShiftReport = (shiftId: number | null) => {
	return useQuery<M1ShiftReport, Error>({
		queryKey: ["m1ShiftReport", shiftId],
		queryFn: ({ signal }) => getM1ShiftReport({ shiftId: shiftId! }, signal),
		enabled: !!shiftId,
		keepPreviousData: true,
		refetchOnWindowFocus: true,
		refetchInterval: 30_000,
	});
};

export const M1ShiftReportPage = () => {
	const [isLoadingPDF, setIsLoadingPDF] = useState(false);
	const [error, setError] = useState("");
	const hasPermissionTo = useHasPermission();
	const canViewIncentive = hasPermissionTo(
		"ui.navbar.mill.incentive.dashboard",
	);
	const [hoveredHeatId, setHoveredHeatId] = useState<number | null>(null);
	const [hoveredDelayId, setHoveredDelayId] = useState<number | null>(null);
	const [hoveredSectionId, setHoveredSectionId] = useState<number | null>(null);
	const navigate = useNavigate();
	const assetId = useAssetId();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const { shiftId: shiftIdParam } = useParams();
	const shiftId = get(shiftInfo, "CurrentShiftId", null) as number | null;
	const shiftIdRef = useRef<number | null>(null);
	shiftIdRef.current = shiftId;

	const {
		data,
		isLoading,
		error: e,
		isError,
		refetch,
	} = useM1ShiftReport(shiftId);

	const handleCrewShiftNavigate = useCallback(
		async (selectedShiftId: number) => {
			if (!assetId) return;

			navigate(`${M1_SHIFT_REPORT_ROUTE}/${selectedShiftId}`);
			await getShiftDataFromAPI(null, selectedShiftId, assetId);
		},
		[assetId, getShiftDataFromAPI, navigate],
	);

	const handleGeneratePDF = async () => {
		try {
			setIsLoadingPDF(true);

			const shiftId = shiftInfo?.CurrentShiftId;
			if (!shiftId) {
				setError("No valid shift selected");
				return;
			}

			const response = await fetch(`/api/m1-shift-reports/pdf/${shiftId}`, {
				method: "POST",
			});

			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || "Error generating the PDF");
			}

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			window.open(url, "_blank", "noopener,noreferrer");
			setTimeout(() => URL.revokeObjectURL(url), 10_000);
		} catch (err: any) {
			setError(err?.message || "Error generating the PDF");
		} finally {
			setIsLoadingPDF(false);
		}
	};

	useMesfRealtime({
		onReceiveMessage: async (_author, message) => {
			const proc = message.replaceAll("[", "").replaceAll("]", "");
			if (proc === "M1.GetShiftReport") {
				await refetch();
			}
		},
	});

	useEffect(() => {
		if (shiftInfo) {
			const newId = get(shiftInfo, "CurrentShiftId", null);
			// Only navigate when the shift navigator moves to a DIFFERENT shift
			// than the URL already shows — avoids a navigate→fetch→navigate loop.
			if (String(newId) !== shiftIdParam) {
				navigate(`${M1_SHIFT_REPORT_ROUTE}/${newId}`);
			}
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!assetId) return;
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId, assetId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0, assetId);
			})();
		}
	}, [assetId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	if (!data) {
		return <M1ShiftReportSkeleton />;
	}

	if (isError) {
		return <ErrorModal error={error} onHide={() => setError("")} />;
	}

	return (
		<>
			<HelmetDexteel title="M1 Shift Report" />
			<Grid2
				container
				spacing={1}
				style={{ padding: "10px 20px", backgroundColor: pageBgColor }}
			>
				<Grid2
					container
					size={12}
					justifyContent="space-between"
					alignItems="center"
				>
					<Grid2
						size={{ md: 4 }}
						alignItems="center"
						sx={{ display: "flex", alignItems: "center", gap: 2 }}
					>
						<Typography
							variant="h5"
							sx={{
								fontWeight: 600,
								userSelect: "none",
							}}
						>
							M1 Shift Report
						</Typography>
						<Typography
							sx={{ fontSize: "0.7rem", color: "#999", fontStyle: "italic" }}
						>
							Work in progress
						</Typography>
					</Grid2>
					<Grid2
						elevation={1}
						size={{ md: 4 }}
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							p: 1,
							mb: 1,
						}}
						component={Paper}
					>
						<Box sx={{ display: "inline-flex" }}>
							<ShiftDayNavigatorControl
								preventInit
								withPaper={false}
								assetId={assetId}
							/>
						</Box>
					</Grid2>
					<Grid2
						size={{ md: 4 }}
						container
						justifyContent="flex-end"
						alignItems="center"
					>
						<ButtonWithLoading
							variant="contained"
							color="primary"
							onClick={handleGeneratePDF}
							isLoading={isLoadingPDF}
							sx={{ whiteSpace: "nowrap", minWidth: 150 }}
						>
							Generate PDF
						</ButtonWithLoading>
					</Grid2>
				</Grid2>

				{/* M1: Shift Summary + KPIs + Incentive + PushRate + RollingTime */}
				<Grid2 container size={12} spacing={1} sx={{ alignItems: "stretch" }}>
					<Grid2 size={{ xs: 3.25 }} component={Paper} sx={{ display: "flex" }}>
						<Box padding={2} sx={{ width: "100%" }}>
							<Typography
								variant="subtitle1"
								style={{ fontWeight: "600" }}
								marginBottom={0.5}
							>
								Shift Summary
							</Typography>
							<M1ProductionStats data={data.ShiftStats} />
						</Box>
					</Grid2>

					<Grid2
						size={{ xs: canViewIncentive ? 4.5 : 5.75 }}
						sx={{ display: "flex" }}
					>
						<KPISection data={data.KpiData} />
					</Grid2>

					{canViewIncentive && (
						<Grid2
							size={{ xs: 1.25 }}
							sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
						>
							<IncentiveCard
								data={data.IncentiveData}
								thresholds={
									data.IncentiveData
										? incentiveThresholds(data.IncentiveData)
										: emptyIncentiveThresholds
								}
							/>
						</Grid2>
					)}

					<Grid2
						size={{ xs: 1.5 }}
						sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
					>
						<PushRateCrewChart
							data={data.PushRateData}
							kpiData={data.KpiData}
							baseRoute="/m1/shift-report"
							onShiftNavigate={handleCrewShiftNavigate}
						/>
					</Grid2>

					<Grid2
						size={{ xs: 1.5 }}
						sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
					>
						<RollingTimeCard data={data.RollingTime} />
					</Grid2>
				</Grid2>

				{/* Gantt — compact mode, no "See on Gantt" button (no standalone M1 Gantt) */}
				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2} style={{ position: "relative" }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Gantt
							</Typography>
						</Box>

						{data.GanttDelays && data.GanttProduction && (
							<GanttChart
								isLoading={isLoading}
								refetch={refetch}
								start={shiftInfo?.CurrentStart!}
								end={shiftInfo?.CurrentEnd!}
								productionData={data.GanttProduction as Production[]}
								delayData={data.GanttDelays as Delay[]}
								gapTimeData={data.GanttGapTime as GapTimeData[]}
								rollChangeData={data.RollChangeIntervals as RollChange[]}
								dmhEventData={data.DmhEvents as DmhEvent[]}
								compactMode
								onHeatHover={setHoveredHeatId}
								hoveredHeatId={hoveredHeatId}
								onDelayHover={setHoveredDelayId}
								hoveredDelayId={hoveredDelayId}
								onSectionHover={setHoveredSectionId}
								hoveredSectionId={hoveredSectionId}
							/>
						)}
					</Box>
				</Grid2>

				{/* Production Summary */}
				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							Production Summary
						</Typography>
						<Grid2 height="20vh">
							<TableProductionSummary
								productionSummary={data.ProductionSummary}
								hoveredSectionId={hoveredSectionId}
								onSectionHover={setHoveredSectionId}
								hiddenColumns={M1_HIDDEN_COLUMNS}
							/>
						</Grid2>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", textAlign: "right", mt: 0.5 }}
						>
							Values in parentheses represent the full campaign totals,
							including production outside this shift.
						</Typography>
					</Box>
				</Grid2>

				{/* Heats table */}
				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							Heats
						</Typography>
						<Grid2 height="30vh">
							<TableHeats
								heats={data.Heats}
								hoveredHeatId={hoveredHeatId}
								onHeatHover={setHoveredHeatId}
								hiddenColumns={M1_HIDDEN_COLUMNS}
							/>
						</Grid2>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", textAlign: "right", mt: 0.5 }}
						>
							Values in parentheses represent the full campaign totals,
							including production outside this shift.
						</Typography>
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							{COOLING_BED_PRODUCTION_LABEL}
						</Typography>
						<Grid2 height="30vh">
							<TableM1StackerBundles bundles={data.StackerBundles} />
						</Grid2>
					</Box>
				</Grid2>

				{/* M1: NO Carrier Changes / Roll Changes section */}

				{/* Top 5 Delays */}
				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Top 5 Delays
							</Typography>
							<Button
								size="small"
								variant="text"
								onClick={() =>
									window.open(`${routeDelaysManager}/${shiftId}`, "_blank")
								}
							>
								See on Delays
							</Button>
						</Box>
						<Grid2 height="30vh">
							<TableDelays
								delays={data.Delays}
								hoveredDelayId={hoveredDelayId}
								onDelayHover={setHoveredDelayId}
							/>
						</Grid2>
					</Box>
				</Grid2>

				{/* Logbook */}
				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Logbook
							</Typography>
							<Button
								size="small"
								variant="text"
								onClick={() =>
									window.open(`${routeLogbookEntry}/${shiftId}`, "_blank")
								}
							>
								See on Logbook
							</Button>
						</Box>
						<Grid2 height="35vh">
							<TableLogbook assetId={M1_LOGBOOK_ASSET_ID} />
						</Grid2>
					</Box>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
